import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Card from './Card';

export interface ModalCardProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  className?: string;
  headerActions?: React.ReactNode;
}

const ModalCard: React.FC<ModalCardProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = 'md',
  closeOnBackdrop = true,
  showCloseButton = true,
  className = '',
  headerActions,
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl',
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div className={`relative w-full ${sizeClasses[size]} max-h-[90vh] ${className}`}>
        <Card variant="default" size="full" hover={false} className="overflow-hidden">
          {/* Header */}
          {(title || subtitle || showCloseButton || headerActions) && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  {title && <h3 className="text-xl onyx-bold text-gray-900 mb-1">{title}</h3>}
                  {subtitle && <p className="text-sm text-gray-600 onyx-body">{subtitle}</p>}
                </div>

                <div className="flex items-center space-x-2">
                  {headerActions}
                  {showCloseButton && (
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)]">{children}</div>
        </Card>
      </div>
    </div>
  );
};

export default ModalCard;
